/**
 * Shared list sorting (DNKADM-0430): the client-side sort helper and the small pure
 * functions behind every sortable column header and its URL state.
 *
 * Kept in a `.ts` module (not beside the components) so `react-refresh/only-export-components`
 * stays clean, and so the rules are unit-testable without rendering.
 *
 * The rules every list follows:
 * - **Deterministic.** Strings compare case- and accent-insensitively with numeric
 *   awareness ("Bay 2" before "Bay 10"); numbers, booleans and dates compare by value.
 * - **Blanks last.** `null`, `undefined`, `''`, `NaN` and an invalid `Date` sort after
 *   every real value in BOTH directions, so flipping the direction never floats the
 *   blanks to the top.
 * - **Stable tiebreak.** Equal values fall back to the optional `tiebreak` key (always
 *   ascending), then to the input order, so a re-render never shuffles equal rows.
 */

export type SortDir = 'asc' | 'desc';

/** What a column accessor may return. Dates may be a `Date` or epoch milliseconds. */
export type SortValue = string | number | boolean | Date | null | undefined;

/** The active sort: which column and which direction. */
export interface SortState<K extends string = string> {
  key: K;
  dir: SortDir;
}

const COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/** Normalize a value for comparison; `null` means "blank" (sorts last). */
function normalize(value: SortValue): string | number | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Compare two NON-blank normalized values ascending. Mixed types (a number against a
 * string) compare as strings, so the order is still total.
 */
function compareNormalized(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a === b ? 0 : a < b ? -1 : 1;
  return COLLATOR.compare(String(a), String(b));
}

/**
 * Compare two values in the given direction with blanks last in BOTH directions.
 * Returns a negative number when `a` sorts first, positive when `b` does, 0 when equal.
 */
export function compareSortValues(a: SortValue, b: SortValue, dir: SortDir = 'asc'): number {
  const na = normalize(a);
  const nb = normalize(b);
  if (na === null || nb === null) {
    if (na === nb) return 0;
    return na === null ? 1 : -1;
  }
  const result = compareNormalized(na, nb);
  return dir === 'asc' ? result : -result;
}

/**
 * Return a sorted COPY of `rows` (the input is never mutated).
 *
 * @param value    the column accessor: what the row shows in the sorted column. Sort by
 *                 the DISPLAYED label when the cell shows a derived label (a badge, a
 *                 humanized enum), not the raw wire value.
 * @param dir      `asc` or `desc`.
 * @param tiebreak optional secondary key, always ascending (an id, or the name). Equal
 *                 rows after it keep their input order.
 */
export function sortRows<T>(
  rows: readonly T[],
  value: (row: T) => SortValue,
  dir: SortDir,
  tiebreak?: (row: T) => SortValue,
): T[] {
  return rows
    .map((row, index) => ({ row, index, primary: value(row), secondary: tiebreak?.(row) }))
    .sort(
      (a, b) =>
        compareSortValues(a.primary, b.primary, dir) ||
        (tiebreak ? compareSortValues(a.secondary, b.secondary, 'asc') : 0) ||
        a.index - b.index,
    )
    .map((entry) => entry.row);
}

/**
 * An ISO timestamp (or date) as epoch milliseconds for a date column, or `null` when
 * absent or unparseable (so it sorts last).
 */
export function toTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * The header click rule: clicking the active column flips its direction; clicking a
 * different column sorts it ascending.
 */
export function toggleSort<K extends string>(current: SortState<K>, key: K): SortState<K> {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: 'asc' };
}

/**
 * Read a sort from its two URL params. An unknown column or direction falls back to
 * `fallback` (a hand-edited or stale link never breaks the screen). A known column with
 * a missing or bad `dir` sorts ascending.
 */
export function parseSort<K extends string>(
  sort: string | null | undefined,
  dir: string | null | undefined,
  allowed: readonly K[],
  fallback: SortState<K>,
): SortState<K> {
  const key = allowed.find((k) => k === sort);
  if (key === undefined) return fallback;
  return { key, dir: dir === 'desc' ? 'desc' : 'asc' };
}

/**
 * The URL patch for a sort: `null` for both params when it equals the screen's default,
 * so a default view keeps a clean link. Pass the result to `useListUrlState`'s `set`.
 */
export function toSortParams<K extends string>(
  next: SortState<K>,
  fallback: SortState<K>,
): { sort: string | null; dir: string | null } {
  if (next.key === fallback.key && next.dir === fallback.dir) return { sort: null, dir: null };
  return { sort: next.key, dir: next.dir };
}
